import { useState } from 'react';
import { Alert, Anchor, Text } from '@mantine/core';
import { IconAlertTriangle, IconCloudOff } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useOfflineKitStatus } from './useOfflineKitStatus';
import { dismissKey } from './staleness';
import { formatDate, pluralize } from './format';

/**
 * Tells an admin when their offline copy of the playbooks no longer matches.
 *
 * Two levels only. Changes since the last download are informational and can
 * be dismissed for the session. No kit at all, or one past the 90-day floor,
 * is a warning that stays until a fresh download clears it.
 */
export function OfflineKitBanner({ isAdmin }: { isAdmin: boolean }) {
  const navigate = useNavigate();
  const { data: status, staleness } = useOfflineKitStatus(isAdmin);
  const [dismissedNow, setDismissedNow] = useState(false);

  // Ambient, so it fails quietly: an error here must never crowd the dashboard.
  if (!status || !staleness || staleness.level === 'none') return null;

  const goToSettings = (
    // A button, not a bare anchor: an anchor with no href is skipped by the
    // keyboard, and this is the one action the banner exists to prompt.
    <Anchor component="button" type="button" size="sm" fw={600} onClick={() => navigate('/settings')}>
      Download a fresh kit
    </Anchor>
  );

  if (staleness.level === 'info') {
    const key = dismissKey(status);
    if (dismissedNow || readDismissed(key)) return null;

    return (
      <Alert
        icon={<IconCloudOff size={16} />}
        color="blue"
        variant="light"
        withCloseButton
        closeButtonLabel="Dismiss for this session"
        onClose={() => {
          writeDismissed(key);
          setDismissedNow(true);
        }}
      >
        <Text size="sm">
          {pluralize(staleness.changedFlows, 'flow has', 'flows have')} changed since your
          Offline Kit was downloaded on {formatDate(status.lastExport!.at)}. {goToSettings} so
          your offline playbooks match.
        </Text>
      </Alert>
    );
  }

  return (
    <Alert icon={<IconAlertTriangle size={16} />} color="orange" variant="light">
      <Text size="sm">
        {staleness.reason === 'never'
          ? 'Your workspace has no Offline Kit. If the app is unreachable during an outage, your team will have no copy of its playbooks.'
          : `Your Offline Kit is ${staleness.ageDays} days old${
              staleness.changedFlows > 0
                ? `, and ${pluralize(staleness.changedFlows, 'flow has', 'flows have')} changed since`
                : ''
            }.`}{' '}
        {goToSettings}.
      </Text>
    </Alert>
  );
}

// sessionStorage can throw (private windows, blocked storage). A banner that
// reappears is a far better failure than a dashboard that crashes.
function readDismissed(key: string): boolean {
  try {
    return sessionStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

function writeDismissed(key: string): void {
  try {
    sessionStorage.setItem(key, '1');
  } catch {
    // Dismissal still holds for this page view via component state.
  }
}
